import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    CONFIRM_DIALOG_MESSAGE_CANCEL,
    CONFIRM_DIALOG_TITLE_CANCEL,
    JS_BASE_URL,
    SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED,
    SNACK_BAR_MESSAGE_URL_SAVED,
} from '../../common/constants';
import { updateUserData, checkUserName } from '../../common/services/mySettingsServices';
import { moderateUserTextInput } from '../../common/sightEngineHelper';
import { checkSpaceInString } from '../../common/textHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdRouterPrompt from '../../common/elemental/AfitdRouterPrompt';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Utilities (lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _words from 'lodash/words';
// import _toLower from 'lodash/toLower';

const useStyles = makeStyles(theme => ({
    textFieldDisabled: {
        backgroundColor: '#F0F2F6',
    },
    buttonBox: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '10px 0',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column-reverse',
            justifyContent: 'center',
            padding: '0px',
        },
    },
    heading: {
        fontWeight: 'bold',
        paddingBottom: 20,
    },
    url: {
        color: 'rgba(17, 30, 20, 0.4)',
        padding: '5px 0',
        [theme.breakpoints.down('sm')]: { marginBottom: 40 },
    },

    linkFieldContainer: { display: 'flex', marginTop: 30, [theme.breakpoints.down('sm')]: { flexDirection: 'column' } },
    linkURLFieldContainer: { [theme.breakpoints.up('md')]: { paddingRight: '5px', width: 270 }, [theme.breakpoints.down('sm')]: { marginBottom: 16 } },
    linkUserNameFieldContainer: { flex: 1 },
    rightMarginToLeftButton: { marginRight: 16 },
    errorMessage: { color: 'red', fontStyle: 'italic' },
    myLinkCancelButton: { marginRight: 16, [theme.breakpoints.down('sm')]: { width: '100%', marginRight: 0 } },
    myLinkSaveButton: { [theme.breakpoints.down('sm')]: { width: '100%', marginBottom: 16 } },
}));

const getDefns = params => {
    const {
        classes,
        userName,
        isDirty,
        // setIsDirty,
        handleSaveButton,
        // currentUser,
        error,
        openBadWordsDialog,
        badWords,
        clearOutBadWords,
        saveLoading,
        handleChangeUserName,
        confirmModalOpen,
        setConfirmModalOpen,
        handleClickConfirmCancelModalYesButton,
    } = params;
    const { textFieldDisabled: textFieldDisabledCls } = classes;
    //rightMarginToLeftButton: rightMarginToLeftButtonCls

    // const isDirty = _toLower(userName) !== _toLower(currentUser.userName || '') && !checkSpaceInString(userName);
    const badWordsList = badWords?.profanity?.join(', ');

    const errorMessageIsHidden = !error;

    // const isModalButtonActive = userName === (currentUser.userName || '');

    const defns = {
        // LINK SECTION
        heading: { variant: 'h6', value: `Create your own A Foot in the Door URL!` },
        insText: { variant: 'body1' },
        linkFieldContainer: {},
        linkURLFieldContainer: {},
        linkUserNameFieldContainer: {},
        linkUrlField: {
            placeholder: `${JS_BASE_URL}/`,
            disabled: true,
            InputProps: {
                className: textFieldDisabledCls,
            },
        },
        url: { variant: 'body2' },
        linkUserNameField: {
            placeholder: 'User Name',
            value: userName,
            onChange: handleChangeUserName, //evt => setUserName(evt.target.value),
            error: checkSpaceInString(userName),
            helperText: checkSpaceInString(userName) ? 'Should not contain space' : null,
        },
        myLinkCancelButton: {
            disabled: !isDirty,
            onClick: () => {
                setConfirmModalOpen(true);
            },
        }, // className: rightMarginToLeftButtonCls
        myLinkSaveButton: { disabled: !isDirty, onClick: handleSaveButton, color: 'primary', loading: saveLoading },
        errorMessage: { hidden: errorMessageIsHidden },

        buttonBox: {},
        cancelModal: {
            title: CONFIRM_DIALOG_TITLE_CANCEL,
            onClose: () => setConfirmModalOpen(false),
            onConfirm: handleClickConfirmCancelModalYesButton,
            open: confirmModalOpen,
            message: CONFIRM_DIALOG_MESSAGE_CANCEL,
        },
        badWordsDialog: {
            title: 'Inappropriate Text is not Allowed!',
            children: (
                <AfitdTypography>
                    The word(s): <b>{badWordsList} </b>
                    does not meet our community guidelines. Inappropriate content cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: openBadWordsDialog,
            onClose: () => clearOutBadWords(),
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

const Link = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { currentUser, userId } = appContext;
    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;
    const classes = useStyles();

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [userName, setUserName] = useState('');
    const [error, setError] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [openBadWordsDialog, setOpenBadWordsDialog] = useState(false);
    const [badWords, setBadWords] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        setUserName(currentUser?.userName || '');
    }, [currentUser]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleClickConfirmCancelModalYesButton = () => {
        setUserName(currentUser?.userName || '');
        setIsDirty(false);
    };

    const handleSaveButton = async () => {
        setSaveLoading(true);
        const res = _words(userName);
        const response = await moderateUserTextInput({ text: JSON.stringify(res) });
        setSaveLoading(false);

        if (!response) {
            setSnackbarErrorMessage(SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED);
        } else if (response?.profanity?.length > 0 || response?.personal?.length > 0 || response?.link?.length > 0) {
            setOpenBadWordsDialog(true);
            setBadWords(response);
        } else {
            if (!checkSpaceInString(userName)) {
                setSaveLoading(true);
                const exists = await checkUserName({ userName });

                !exists &&
                    (await updateUserData({
                        data: { ...currentUser, userName },
                        userId,
                        setSnackbarMessage,
                        setSnackbarErrorMessage,
                        snackBarMessage: SNACK_BAR_MESSAGE_URL_SAVED,
                    }));
                setError(exists);
                setSaveLoading(false);
                setIsDirty(false);
            }
        }
    };
    const clearOutBadWords = () => {
        let badWord = badWords?.profanity?.join('');
        badWord.toLowerCase();

        const regEx = new RegExp(badWord, 'ig');

        if (userName.toLowerCase().includes(badWord)) {
            const filteredUserName = userName.replace(regEx, '');
            setUserName(filteredUserName);
        }
        setOpenBadWordsDialog(false);
        // setBadWords({});
    };

    const handleChangeUserName = evt => {
        setError(null);
        setIsDirty(true);
        setUserName(evt.target.value);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        userName,
        isDirty,
        // setIsDirty,
        handleSaveButton,
        currentUser,
        error,
        openBadWordsDialog,
        badWords,
        clearOutBadWords,
        saveLoading,
        handleChangeUserName,
        confirmModalOpen,
        setConfirmModalOpen,
        handleClickConfirmCancelModalYesButton,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div>
            <AfitdRouterPrompt when={isDirty} />
            <AfitdTypography {...defns.heading} />
            <AfitdTypography {...defns.insText}>
                Choose a URL that describes you in a concise way. Be sure to make it short and easy to remember so you can share your profile link with ease. This link will be
                visible to employers, so please select a URL that represents you well.
            </AfitdTypography>

            <div {...defns.linkFieldContainer}>
                <div {...defns.linkURLFieldContainer}>
                    <AfitdTextField {...defns.linkUrlField} />
                </div>
                <div {...defns.linkUserNameFieldContainer}>
                    <AfitdTextField {...defns.linkUserNameField} />
                </div>
            </div>

            <AfitdTypography {...defns.url}>
                {`Your Public URL is: ${JS_BASE_URL}/${userName}`}
                <span {...defns.errorMessage}> is already in use. Please select another URL</span>
            </AfitdTypography>

            <div className={classes.buttonBox}>
                <AfitdButton {...defns.myLinkCancelButton}>Cancel</AfitdButton>
                <AfitdButton {...defns.myLinkSaveButton}>Save Changes</AfitdButton>
            </div>
            <AfitdConfirmDialog {...defns.cancelModal} />
            <AfitdMessageDialog {...defns.badWordsDialog} />
        </div>
    );
};

export default Link;
